import { z } from 'zod';
import { toast } from 'sonner';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useChangePasswordForm } from '../useAuthForm';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  useChangePasswordMutation,
  useLazyGetUserQuery,
} from '../auth.api.slice';
import { changePasswordSchema } from '../auth.schema';
import { setShowLoginDialog } from '@/redux/global/global.slice';

function ChangePasswordForm() {
  const changePasswordForm = useChangePasswordForm();
  const [changePassword, { isLoading: isChangePasswordLoading }] =
    useChangePasswordMutation();
  const [getCurrentUser] = useLazyGetUserQuery();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  async function handleChangePasswordSubmit(
    values: z.infer<typeof changePasswordSchema>
  ) {
    try {
      const data = await changePassword({
        newPassword: values.newPassword,
        currentPassword: values.currentPassword,
      }).unwrap();

      if (data.status !== 'success') {
        toast.error('Change Password Failed', {
          description: (data as any)?.errorMessage || 'Something went wrong',
        });
      }
      await getCurrentUser();
      navigate('/');
      dispatch(setShowLoginDialog(true));
      toast.success('Password Changed successfully');
    } catch (error: any) {
      toast.error('Change Password Failed', {
        description: error?.data?.errorMessage || 'Something went wrong',
      });
    }
  }

  return (
    <Form {...changePasswordForm}>
      <form
        onSubmit={changePasswordForm.handleSubmit(handleChangePasswordSubmit)}
      >
        <div>
          <h1 className="font-primary text-lg font-bold text-center">
            Change Your Password
          </h1>
          <p className="text-center font-secondary text-[16px] text-[#666666]">
            Enter your current and new password to update it.
          </p>
        </div>
        <div className="flex flex-col p-2 gap-4">
          <div className="flex flex-col gap-2">
            <FormField
              control={changePasswordForm.control}
              name="currentPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="flex flex-col p-2 gap-4">
          <div className="flex flex-col gap-2">
            <FormField
              control={changePasswordForm.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="Enter your password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <FormField
              control={changePasswordForm.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm New Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="Confirm your password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="mt-2">
          <div className="flex flex-col gap-2 w-full">
            <Button
              type="submit"
              className="w-full bg-black font-bold"
              disabled={isChangePasswordLoading}
            >
              Change Password
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

export default ChangePasswordForm;
